import { MutableRefObject } from 'react';

// Shared Cognito access-token manager used by both apps/hub and apps/admin.
//
// The app injects `getAuthTokens` (a thin wrapper over Amplify's
// `fetchAuthSession`), so this hook stays free of any auth-library import.
// `fetchAndSetAuthTokens` writes the latest access token into the caller's
// `token` ref; reading that ref lazily (e.g. from the websocket URL builder)
// is what keeps long-lived connections from replaying an expired token.
const useAuthTokens = ({
  token,
  getAuthTokens,
}: {
  token: MutableRefObject<string>;
  getAuthTokens: (options?: any) => Promise<any>;
}) => {
  const fetchAndSetTokens = async (options?: any) => {
    const sessionTokens = await getAuthTokens(options);

    const accessToken = sessionTokens?.accessToken.toString();

    if (!accessToken) {
      token.current = '';
      throw new Error('No access token');
    }

    token.current = accessToken;

    return sessionTokens;
  };

  const calculateDurationBeforeRefreshingToken = (
    expFromToken: number,
  ): number => {
    const computedDateFromExp = new Date(expFromToken * 1000);
    const nowDate = new Date();
    return computedDateFromExp.getTime() - nowDate.getTime();
  };

  const refreshToken = (delay: number) => {
    const expFromToken = calculateDurationBeforeRefreshingToken(delay);

    setTimeout(async () => {
      const sessionTokens = await fetchAndSetTokens({ forceRefresh: true });

      const exp = sessionTokens?.accessToken?.payload?.exp;
      if (exp) {
        refreshToken(exp);
      }
    }, expFromToken - 30000);
  };

  return {
    refreshAuthToken: refreshToken,
    fetchAndSetAuthTokens: fetchAndSetTokens,
  };
};

export default useAuthTokens;
